import { useCallback, useEffect, useState } from 'react';
import '../../common/Mypage.scss';
import '../../../ForListPage.scss';
import '../../../recruit/common/RecruitList.scss';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import MainURL from '../../../../MainURL';
import ImageURL from '../../../../ImageURL';
import Loading from '../../../../components/Loading';
import { useAtom } from 'jotai';
import { userDataAtom } from '../../../../AtomStore';
import { useDropzone } from 'react-dropzone';
import imageCompression from "browser-image-compression";
import { DaumPostcodeEmbed } from 'react-daum-postcode';
import { CiCircleMinus } from 'react-icons/ci';
import { citydata } from '../../../../DefaultData';
import '../../../recruit/common/Recruit.scss';
import MypageMenu from '../../common/MypageMenu';
import type { SelfIntroItem } from '../../../minister/pages/ministerInfo/ministerInfoTypes';
import {
  PortfolioEdit,
  RESUME_SECTION_IDS,
  SelfIntroductionEdit,
  buildPortfolioPayload,
  findIncompletePortfolio,
  withDefaultIntroQuestions,
} from './ResumeExtrasEdit';
import type { PortfolioEditItem } from './ResumeExtrasEdit';

interface EducationItem {
  schoolName: string;
  major: string;
  startDate: string;
  endDate: string;
  status: string;
  description: string;
}

interface CareerItem {
  companyName: string;
  employType?: string;
  position: string;
  startDate: string;
  endDate: string;
  description: string;
}

const CAREER_EMPLOY_TYPES = ['전임', '준전임', '파트'];

// 희망 역할 / 희망 부서 입력창 아래 예시 버튼
const HOPE_ROLE_EXAMPLES = ['담임목사', '부목사', '강도사', '전도사', '교육전도사', '찬양사역자', '반주자', '행정간사'];
const HOPE_DEPARTMENT_EXAMPLES = ['영아부', '유치부', '유초등부', '중고등부', '청년부', '장년부', '교구', '찬양팀', '선교부'];

const INSURANCE_ITEMS = ['건강보험', '고용보험', '국민연금', '산재보험'];

// 쉼표로 구분된 문자열 ↔ 배열 (희망 유형·역할·부서, 경력 구분)
const splitComma = (value: string | undefined | null) => String(value || '').split(',').map((v) => v.trim()).filter(Boolean);
const toggleComma = (value: string, item: string) => {
  const list = splitComma(value);
  return (list.includes(item) ? list.filter((v) => v !== item) : [...list, item]).join(', ');
};

function ExampleChips({ examples, value, onChange }: { examples: string[]; value: string; onChange: (next: string) => void }) {
  const selected = splitComma(value);
  return (
    <div className='subTextBox'>
      {examples.map((example) => (
        <p key={example} className={`subText ${selected.includes(example) ? 'selected' : ''}`}
          onClick={() => onChange(toggleComma(value, example))}>
          {example}
        </p>
      ))}
    </div>
  );
}

// 예전에는 구분 선택값이 사역내용(position)에 저장됐다 — 구분 값만 들어있으면 employType 으로 옮긴다
function normalizeCareerItems(items: CareerItem[] | undefined | null): CareerItem[] {
  if (!Array.isArray(items)) return [];
  return items.map((item) => (
    !item.employType && CAREER_EMPLOY_TYPES.includes(item.position)
      ? { ...item, employType: item.position, position: '' }
      : { ...item, employType: item.employType || '' }
  ));
}

// 'AI작성하기'로 폼 전체를 한 번에 채울 때 넘기는 값
export interface ResumeFillData {
  title: string;
  name: string;
  gender: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  ageText: string;
  phone: string;
  email: string;
  address: string;
  addressDetail: string;
  profileFile: File | null;
  educationItems: EducationItem[];
  careerItems: CareerItem[];
  hopeType: string;
  hopeLocation: string;
  hopeSalary: string;
  hopeSalaryPaySort: string;
  hopeRoles: string;
  hopeDepartment: string;
  hopeInsurance: string;
  hopeWelfare: string;
  selfIntroduction: SelfIntroItem[];
  portfolioItems: PortfolioEditItem[];
  isPublic: boolean;
}

type ResumeVisibility = 'PUBLIC' | 'APPLIED' | 'PRIVATE';
const VISIBILITY_OPTIONS: { value: ResumeVisibility; label: string; desc: string }[] = [
  { value: 'PUBLIC', label: '전체공개', desc: "교회 담당자 회원 모두가 '사역자정보'에서 이력서를 열람할 수 있습니다. 목록에는 이름 일부만 보입니다." },
  { value: 'APPLIED', label: '지원한 교회만', desc: "사역자정보 목록에는 나오지만, 이력서는 내가 지원한 교회에만 보입니다." },
  { value: 'PRIVATE', label: '비공개', desc: "사역자정보 목록에는 나오지만, 클릭하면 '비공개'로 안내되고 이력서는 보이지 않습니다. 인재 검색(스카우트)에도 나오지 않습니다." },
];

// 첨부 이력서 자동 변환 결과 (server routers/resume/applyRelay.js POST /applyrelay/parse)
export interface ResumePrefill {
  title?: string;
  name?: string;
  gender?: string;
  birthYear?: string;
  birthMonth?: string;
  birthDay?: string;
  phone?: string;
  email?: string;
  address?: string;
  educationItems?: EducationItem[];
  careerItems?: CareerItem[];
  hopeRoles?: string;
  hopeDepartment?: string;
  selfIntroduction?: string;
  sermonLinks?: string[];
  sourceFile?: string;
}

// 관리자 페이지(사역자정보 관리)에서 재사용할 때 — 로그인 회원 대신 지정 계정으로 작성/수정
export interface ResumeEditAdminOptions {
  userAccount: string;
  signWriter: string;
  resumeId?: string;
  onDone: () => void;
  // 있으면 상단에 'AI작성하기' 버튼 노출 — 채울 값을 돌려주고, 없으면 null
  onAiFill?: () => Promise<ResumeFillData | null>;
}

const COMMON_EMAIL_DOMAINS = ['naver.com', 'gmail.com', 'daum.net', 'hanmail.net', 'kakao.com', 'outlook.com', 'yahoo.com'];

// 쉼표로 구분된 희망지역 문자열 → 배열
function parseHopeLocation(value: unknown): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value) return value.split(',').map(loc => loc.trim()).filter(loc => loc);
  return [];
}

export default function ResumeEdit({ admin }: { admin?: ResumeEditAdminOptions } = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const resumeId = admin ? admin.resumeId || null : new URLSearchParams(location.search).get('id');
  // 사역자정보 작성 안내에서 넘어올 때 해당 섹션으로 스크롤 (?section=intro|portfolio)
  const sectionParam = new URLSearchParams(location.search).get('section') as keyof typeof RESUME_SECTION_IDS | null;
  const [loginUserData] = useAtom(userDataAtom);
  const userData = admin
    ? { ...loginUserData, userAccount: admin.userAccount, userNickName: admin.signWriter }
    : loginUserData;
  const isEditMode = !!resumeId;

  // 기본 정보
  const [title, setTitle] = useState('');
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [ageText, setAgeText] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [email2Custom, setEmail2Custom] = useState('');
  const [address, setAddress] = useState('');
  const [addressDetail, setAddressDetail] = useState('');
  // 공통 데이터 — 모든 이력서·프로필페이지가 함께 쓴다 (server/routers/minister/ministerCommon.js)
  const [church, setChurch] = useState('');
  const [religiousbody, setReligiousbody] = useState('');
  const [dutyTitle, setDutyTitle] = useState('');
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [isViewAddress, setIsViewAddress] = useState(false);

  // 배열 필드
  const [educationItems, setEducationItems] = useState<EducationItem[]>([]);
  const [careerItems, setCareerItems] = useState<CareerItem[]>([]);

  // 자기소개서 / 사역 포트폴리오
  const [selfIntroduction, setSelfIntroduction] = useState<SelfIntroItem[]>(() => withDefaultIntroQuestions([]));
  const [portfolioItems, setPortfolioItems] = useState<PortfolioEditItem[]>([]);

  // 희망사항
  const [hopeInfo, setHopeInfo] = useState({
    hopeType: '',
    hopeLocation: [] as string[],
    hopeSalary: '',
    hopeSalaryPaySort: '월',
    hopeRoles: '',
    hopeDepartment: '',
    hopeInsurance: '',
    hopeWelfare: ''
  });
  const isSalaryNegotiable = hopeInfo.hopeSalaryPaySort === '면접 후 협의';
  const [selectedCity, setSelectedCity] = useState<string>('');

  // 기타
  const [saveDate, setSaveDate] = useState('');
  // 사역자정보(/minister) 공개 동의 — 교회 담당자 회원에게만 노출
  const [isPublic, setIsPublic] = useState(false);
  // 공개범위 (전체공개 · 지원한 교회만 · 비공개). '' 는 아직 고르지 않은 기존 이력서
  const [visibility, setVisibility] = useState<'' | ResumeVisibility>('');
  const chooseVisibility = (next: ResumeVisibility) => {
    setVisibility(next);
    setIsPublic(next === 'PUBLIC');
  };
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  // DaumPostcodeEmbed 주소 입력 함수
  const onCompletePost = (data: any) => {
    const copy = data.address;
    setAddress(copy);
    setIsViewAddress(false);
  };

  // 이미지 업로드
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    try {
      setImageLoading(true);
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000
      };
      const resizingBlob = await imageCompression(file, options);
      const regexCopy = /[^a-zA-Z0-9!@#$%^&*()\-_=+\[\]{}|;:'",.<>]/g;
      const userIdCopy = userData?.userAccount.slice(0, 5);
      const regex = resizingBlob.name.replace(regexCopy, '');
      const regexSlice = regex.slice(-15);
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      const newFile = new File([resizingBlob], `${dateStr}${userIdCopy}_${regexSlice}`, {
        type: file.type,
      });
      setProfileImage(newFile);
      setProfileImageUrl(URL.createObjectURL(newFile));
      setImageLoading(false);
    } catch (error) {
      setImageLoading(false);
      console.error('이미지 리사이징 중 오류 발생:', error);
    }
  }, [userData]);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  // phone 파싱 (010-1234-5678 형식 또는 01012345678 형식)
  const applyPhone = (phoneValue: string) => {
    if (!phoneValue) return;
    const phoneParts = phoneValue.replace(/-/g, '').match(/(\d{3})(\d{4})(\d{4})/);
    if (phoneParts) {
      setPhone1(phoneParts[1]);
      setPhone2(phoneParts[2]);
      setPhone3(phoneParts[3]);
    } else {
      const parts = phoneValue.split('-');
      if (parts.length === 3) {
        setPhone1(parts[0]);
        setPhone2(parts[1]);
        setPhone3(parts[2]);
      }
    }
  };

  // email 파싱
  const applyEmail = (emailValue: string) => {
    if (!emailValue) return;
    const emailParts = emailValue.split('@');
    if (emailParts.length !== 2) return;
    setEmail1(emailParts[0]);
    const domain = emailParts[1];
    if (COMMON_EMAIL_DOMAINS.includes(domain)) {
      setEmail2(domain);
      setEmail2Custom('');
    } else {
      setEmail2('직접입력');
      setEmail2Custom(domain);
    }
  };

  // 'AI작성하기' — 관리자 화면에서 넘겨준 값으로 폼 전체를 채운다
  const [aiFilling, setAiFilling] = useState(false);
  const handleAiFill = async () => {
    if (!admin?.onAiFill || aiFilling) return;
    setAiFilling(true);
    try {
      const data = await admin.onAiFill();
      if (!data) return;
      setTitle(data.title);
      setName(data.name);
      setGender(data.gender);
      setBirthYear(data.birthYear);
      setBirthMonth(data.birthMonth);
      setBirthDay(data.birthDay);
      setAgeText(data.ageText);
      setPhone1('');
      setPhone2('');
      setPhone3('');
      applyPhone(data.phone);
      setEmail1('');
      setEmail2('');
      setEmail2Custom('');
      applyEmail(data.email);
      setAddress(data.address);
      setAddressDetail(data.addressDetail);
      setProfileImage(data.profileFile);
      setProfileImageUrl(data.profileFile ? URL.createObjectURL(data.profileFile) : '');
      setEducationItems(data.educationItems);
      setCareerItems(normalizeCareerItems(data.careerItems));
      setHopeInfo({
        hopeType: data.hopeType,
        hopeLocation: parseHopeLocation(data.hopeLocation),
        hopeSalary: data.hopeSalary,
        hopeSalaryPaySort: data.hopeSalaryPaySort || '월',
        hopeRoles: data.hopeRoles,
        hopeDepartment: data.hopeDepartment,
        hopeInsurance: data.hopeInsurance,
        hopeWelfare: data.hopeWelfare,
      });
      setSelfIntroduction(withDefaultIntroQuestions(data.selfIntroduction));
      setPortfolioItems(data.portfolioItems);
      setIsPublic(data.isPublic);
    } catch (error) {
      console.error('AI작성하기 실패:', error);
      alert('자동 작성에 실패했습니다.');
    } finally {
      setAiFilling(false);
    }
  };

  // 지원할 때 올린 이력서 파일에서 뽑은 초안 (공고 상세의 파일 지원 → "사이트 이력서로 옮기기")
  const prefill = !admin && !resumeId ? (location.state as { prefill?: ResumePrefill } | null)?.prefill : undefined;
  const [prefillSource, setPrefillSource] = useState('');

  // 기존 데이터 불러오기
  useEffect(() => {
    if (!resumeId) {
      const today = new Date();
      setSaveDate(today.toISOString().split('T')[0]);
      // 새 이력서 — 공통 데이터(이름·연락처·사진·교회·교단·직분·학력·경력·포트폴리오)로 먼저 채운다
      if (!admin && !prefill && userData.userAccount) {
        axios.get(`${MainURL}/resume/common`, { params: { userAccount: userData.userAccount } })
          .then((res) => {
            const common = res.data?.success ? res.data.common : null;
            if (!common) return;
            setName(common.name || '');
            if (common.phone) applyPhone(common.phone);
            if (common.email) applyEmail(common.email);
            setChurch(common.church || '');
            setReligiousbody(common.religiousbody || '');
            setDutyTitle(common.dutyTitle || '');
            setEducationItems(Array.isArray(common.educationItems) ? common.educationItems : []);
            setCareerItems(normalizeCareerItems(common.careerItems));
            setPortfolioItems(Array.isArray(common.portfolioItems) ? common.portfolioItems : []);
            if (common.profileImage) setProfileImageUrl(`${ImageURL}/images/resume/profile/${common.profileImage}`);
          })
          .catch((err) => console.error('공통 정보 조회 실패:', err));
      }
      if (prefill) {
        setTitle(prefill.title || '');
        setName(prefill.name || '');
        setGender(prefill.gender || '');
        setBirthYear(prefill.birthYear || '');
        setBirthMonth(prefill.birthMonth || '');
        setBirthDay(prefill.birthDay || '');
        if (prefill.phone) applyPhone(prefill.phone);
        if (prefill.email) applyEmail(prefill.email);
        setAddress(prefill.address || '');
        setEducationItems(prefill.educationItems || []);
        setCareerItems(normalizeCareerItems(prefill.careerItems));
        setHopeInfo(prev => ({ ...prev, hopeRoles: prefill.hopeRoles || '', hopeDepartment: prefill.hopeDepartment || '' }));
        if (prefill.selfIntroduction) {
          setSelfIntroduction([{ question: '자기소개', answer: prefill.selfIntroduction }]);
        }
        if (prefill.sermonLinks?.length) {
          setPortfolioItems(prefill.sermonLinks.map(link => ({
            category: '설교영상' as const, title: '설교 영상', church: '', date: '', description: '', link, image: '',
          })));
        }
        setPrefillSource(prefill.sourceFile || '이력서 파일');
      }
      return;
    }
    setLoading(true);
    axios.get(`${MainURL}/resume/getresume/${resumeId}`, {
      params: { userAccount: userData.userAccount }
    })
      .then(res => {
        if (res.data.success && res.data.resume) {
          const data = res.data.resume;
          setTitle(data.title || '');
          setName(data.name || '');
          setGender(data.gender || '');
          setBirthYear(data.birthYear || '');
          setBirthMonth(data.birthMonth || '');
          setBirthDay(data.birthDay || '');
          setAgeText(data.ageText || '');
          applyPhone(data.phone || '');
          applyEmail(data.email || '');
          setAddress(data.address || '');
          setAddressDetail(data.addressDetail || '');
          setChurch(data.church || '');
          setReligiousbody(data.religiousbody || '');
          setDutyTitle(data.dutyTitle || '');
          setEducationItems(data.educationItems || []);
          setCareerItems(normalizeCareerItems(data.careerItems));
          setSelfIntroduction(withDefaultIntroQuestions(data.selfIntroduction));
          setPortfolioItems(Array.isArray(data.portfolioItems) ? data.portfolioItems : []);
          setHopeInfo({
            hopeType: data.hopeType || '',
            hopeLocation: parseHopeLocation(data.hopeLocation),
            hopeSalary: data.hopeSalary || '',
            hopeSalaryPaySort: data.hopeSalaryPaySort || '월',
            hopeRoles: data.hopeRoles || '',
            hopeDepartment: data.hopeDepartment || '',
            hopeInsurance: data.hopeInsurance || '',
            hopeWelfare: data.hopeWelfare || ''
          });
          setSaveDate(data.saveDate || new Date().toISOString().split('T')[0]);
          setIsPublic(data.isPublic === 'true');
          setVisibility(data.visibility || (data.isPublic === 'true' ? 'PUBLIC' : ''));
          
          if (data.profileImage) {
            setProfileImageUrl(`${ImageURL}/images/resume/profile/${data.profileImage}`);
          }
        }
      })
      .catch(err => {
        console.error('이력서 조회 실패:', err);
        alert('이력서를 불러오는데 실패했습니다.');
      })
      .finally(() => setLoading(false));
  }, [resumeId, userData.userAccount]);

  useEffect(() => {
    if (loading || !sectionParam || !RESUME_SECTION_IDS[sectionParam]) return;
    document.getElementById(RESUME_SECTION_IDS[sectionParam])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [loading, sectionParam]);

  const submitLabel = isEditMode ? '수정하기' : '저장하기';

  /** 페이지 하단까지 스크롤되면 저장 버튼을 우측 → 중앙으로 옮김 (사역자 공고 등록과 동일) */
  const [isAtBottom, setIsAtBottom] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      setIsAtBottom(el.scrollHeight - window.scrollY - window.innerHeight < 80);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // 저장/수정 함수
  const handleSave = async () => {
    const phoneValue = phone1 && phone2 && phone3 ? `${phone1}-${phone2}-${phone3}` : '';
    const emailValue = email1 && (email2 === '직접입력' ? email2Custom : email2) ? `${email1}@${email2 === '직접입력' ? email2Custom : email2}` : '';
    
    if (!name || !phoneValue || !emailValue) {
      alert('필수 입력 항목을 입력해주세요. (이름, 휴대폰, 이메일)');
      return;
    }

    if (imageLoading) return;

    const incompleteIndex = findIncompletePortfolio(portfolioItems);
    if (incompleteIndex >= 0) {
      alert(`포트폴리오 ${incompleteIndex + 1}의 제목과 ${portfolioItems[incompleteIndex].category === '사역사진' ? '사진' : '영상 링크'}을 입력해주세요.`);
      return;
    }

    const formData = new FormData();
    if (profileImage) {
      formData.append('profileImage', profileImage);
    }
    const portfolio = buildPortfolioPayload(portfolioItems);
    portfolio.files.forEach((file) => formData.append('portfolioImages', file));

    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    const requestData = {
      ...(isEditMode && { resumeId }),
      userAccount: userData.userAccount,
      title: title,
      name,
      gender,
      birthYear,
      birthMonth,
      birthDay,
      ageText,
      phone: phoneValue,
      email: emailValue,
      address,
      addressDetail,
      church,
      religiousbody,
      dutyTitle,
      educationItems: JSON.stringify(educationItems),
      careerItems: JSON.stringify(careerItems),
      hopeInfo: JSON.stringify({
        ...hopeInfo,
        hopeLocation: Array.isArray(hopeInfo.hopeLocation) ? hopeInfo.hopeLocation.join(', ') : hopeInfo.hopeLocation
      }),
      signWriter: userData?.userNickName || userData?.userAccount || '',
      saveDate: saveDate || dateStr,
      isPublic: isPublic ? 'true' : 'false',
      ...(visibility ? { visibility } : {}),
      selfIntroduction: JSON.stringify(selfIntroduction),
      portfolioItems: JSON.stringify(portfolio.payload)
    };

    // FormData에 모든 필드 추가
    Object.keys(requestData).forEach(key => {
      formData.append(key, requestData[key as keyof typeof requestData] as string);
    });

    try {
      const url = isEditMode ? `${MainURL}/resume/updateresume` : `${MainURL}/resume/saveresume`;
      const res = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.data.success) {
        alert(isEditMode ? '이력서가 수정되었습니다.' : '이력서가 저장되었습니다.');
        if (admin) admin.onDone();
        else navigate('/mypage/resumemanage');
      } else {
        alert(res.data.error || '저장에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('저장 실패:', error);
      alert(error.response?.data?.error || '저장에 실패했습니다.');
    }
  };

  // 배열 항목 추가/삭제 함수들
  const addEducationItem = () => {
    setEducationItems([...educationItems, {
      schoolName: '',
      major: '',
      startDate: '',
      endDate: '',
      status: '',
      description: ''
    }]);
  };

  const removeEducationItem = (index: number) => {
    setEducationItems(educationItems.filter((_, i) => i !== index));
  };

  const addCareerItem = () => {
    setCareerItems([...careerItems, {
      companyName: '',
      employType: '',
      position: '',
      startDate: '',
      endDate: '',
      description: ''
    }]);
  };

  const removeCareerItem = (index: number) => {
    setCareerItems(careerItems.filter((_, i) => i !== index));
  };


  if (loading) {
    return (
      <div className={`mypage${admin ? ' mypage--admin-embed' : ''}`}>
        <div className="inner">
          {!admin && <MypageMenu />}
          <div className="subpage__main">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
              <Loading />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mypage${admin ? ' mypage--admin-embed' : ''}`} style={{ paddingBottom: admin ? 0 : '100px' }}>
      <div className="inner">
        {!admin && <MypageMenu />}
        <div className="subpage__main">
        <div className="mypage-panel mypage-section-card">
          <section style={{ marginBottom: '50px' }}>
            {prefillSource && (
              <div className="resume-prefill-notice">
                <strong>'{prefillSource}'에서 내용을 옮겨 왔습니다.</strong>
                <span>자동으로 읽은 값이라 틀린 곳이 있을 수 있어요. 확인·수정한 뒤 저장하면 다음 지원부터는 클릭 한 번이면 됩니다.</span>
              </div>
            )}
            <div className="main_title_row">
              <h3 className='main_title'>
                {isEditMode ? '이력서 수정' : '이력서 작성'}
              </h3>
              <div className='postBtnbox'>
                {admin?.onAiFill && !isEditMode && (
                  <div className='postBtn' style={{ marginRight: '10px' }} onClick={handleAiFill}>
                    <p>{aiFilling ? '작성 중...' : 'AI작성하기'}</p>
                  </div>
                )}
                {admin?.onAiFill && !isEditMode && (
                  <div className='postBtn' style={{ marginRight: '10px' }} onClick={handleSave}>
                    <p>저장</p>
                  </div>
                )}
                <div className='postBtn' style={{ marginRight: '10px' }} onClick={() => (admin ? admin.onDone() : navigate(-1))}>
                  <p>{admin ? '목록으로' : '뒤로가기'}</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h1 className='section_title'>기본 정보</h1>
            <div className='inputCover'>
              <div className="inputBox">
                <h3 className='title'><p>이력서 제목</p></h3>
                <div className="inputRow">
                  <input value={title} className="inputdefault" type="text"
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="예: 신입 목사 이력서" />
                </div>
              </div>
            </div>
          </section>

          {/* 기본 정보 */}
          <section>
            <h1 className='section_title'>인적사항</h1>
            {!admin && (
              <p className="resume-common-notice">
                이름·사진·연락처·현재 교회·교단·직분·학력·경력·포트폴리오는 모든 이력서와 프로필페이지에 함께 적용됩니다.
              </p>
            )}
            <div className='inputCover'>
              <div className="inputBox" style={{ gridColumn: '1 / -1' }}>
                <h3 className='title'><p>프로필 사진</p></h3>
                <div className="inputRow">
                  {profileImageUrl ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <img src={profileImageUrl} alt="프로필" style={{ width: '150px', height: '200px', objectFit: 'cover', borderRadius: 'var(--r)' }} />
                      <button type="button" onClick={() => { setProfileImage(null); setProfileImageUrl(''); }}
                        style={{ padding: '8px 16px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', cursor: 'pointer' }}>
                        삭제
                      </button>
                    </div>
                  ) : (
                    <div {...getRootProps()} style={{ width: '130px', height: '200px',
                      padding: '20px', border: '2px dashed var(--rule-2)', borderRadius: 'var(--r)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input {...getInputProps()} />
                      {imageLoading ? <p>이미지 처리 중...</p> : <p>이미지 첨부</p>}
                    </div>
                  )}
                </div>
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>이름 *</p></h3>
                  <div className="inputRow">
                    <input value={name} className="inputdefault" type="text"
                      onChange={(e) => setName(e.target.value)} required />
                  </div>
                </div>
                <div className="inputBox">
                  <h3 className='title'><p>성별</p></h3>
                  <div className="inputRow">
                    <div className='checkTextRow'>
                      <div className='checkTextBox' onClick={() => setGender('남')}>
                        <input className="checkbox" type="radio" name="gender" value="남"
                          checked={gender === '남'} onChange={() => setGender('남')} />
                        <span className="checkmark" />
                        <div className='imgtextBox'>
                          <p className={gender === '남' ? 'selected' : ''}>남</p>
                        </div>
                      </div>
                      <div className='checkTextBox' onClick={() => setGender('여')}>
                        <input className="checkbox" type="radio" name="gender" value="여"
                          checked={gender === '여'} onChange={() => setGender('여')} />
                        <span className="checkmark" />
                        <div className='imgtextBox'>
                          <p className={gender === '여' ? 'selected' : ''}>여</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>생년월일</p></h3>
                  <div className="inputRow" style={{ display: 'flex', gap: '10px' }}>
                    <select value={birthYear} className="inputdefault" style={{ width: '40%' }}
                      onChange={(e) => setBirthYear(e.target.value)}>
                      <option value="">년</option>
                      {Array.from({ length: 51 }, (_, i) => 1960 + i).map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <select value={birthMonth} className="inputdefault" style={{ width: '30%' }}
                      onChange={(e) => setBirthMonth(e.target.value)}>
                      <option value="">월</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                        <option key={month} value={month}>{month}</option>
                      ))}
                    </select>
                    <select value={birthDay} className="inputdefault" style={{ width: '30%' }}
                      onChange={(e) => setBirthDay(e.target.value)}>
                      <option value="">일</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="inputBox">
                  <h3 className='title'><p>나이 (만 나이)</p></h3>
                  <div className="inputRow">
                    <select value={ageText} className="inputdefault"
                      onChange={(e) => setAgeText(e.target.value)}>
                      <option value="">선택</option>
                      {Array.from({ length: 43 }, (_, i) => 18 + i).map(age => (
                        <option key={age} value={age}>{age}세</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>휴대폰 *</p></h3>
                  <div className="inputRow" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input 
                      value={phone1} 
                      className="inputdefault" 
                      type="text"
                      maxLength={3}
                      style={{ width: '80px' }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 3);
                        setPhone1(value);
                      }} 
                      required 
                    />
                    <span style={{ fontSize: '16px' }}>-</span>
                    <input 
                      value={phone2} 
                      className="inputdefault" 
                      type="text"
                      maxLength={4}
                      style={{ width: '100px' }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                        setPhone2(value);
                      }} 
                      required 
                    />
                    <span style={{ fontSize: '16px' }}>-</span>
                    <input 
                      value={phone3} 
                      className="inputdefault" 
                      type="text"
                      maxLength={4}
                      style={{ width: '100px' }}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                        setPhone3(value);
                      }} 
                      required 
                    />
                  </div>
                </div>
                <div className="inputBox">
                  <h3 className='title'><p>이메일 *</p></h3>
                  <div className="inputRow" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input 
                      value={email1} 
                      className="inputdefault" 
                      type="text"
                      style={{ flex: 1, minWidth: '120px' }}
                      onChange={(e) => {
                        setEmail1(e.target.value);
                      }} 
                      required 
                    />
                    <span style={{ fontSize: '16px' }}>@</span>
                    {email2 === '직접입력' ? (
                      <input 
                        value={email2Custom} 
                        className="inputdefault" 
                        type="text"
                        style={{ flex: 1, minWidth: '150px' }}
                        placeholder="메일 주소 입력"
                        onChange={(e) => {
                          setEmail2Custom(e.target.value);
                        }} 
                        required
                      />
                    ) : (
                      <select 
                        value={email2} 
                        className="inputdefault" 
                        style={{ flex: 1, minWidth: '150px' }}
                        onChange={(e) => {
                          setEmail2(e.target.value);
                          setEmail2Custom('');
                        }}
                        required
                      >
                        <option value="">선택</option>
                        <option value="naver.com">naver.com</option>
                        <option value="gmail.com">gmail.com</option>
                        <option value="daum.net">daum.net</option>
                        <option value="hanmail.net">hanmail.net</option>
                        <option value="kakao.com">kakao.com</option>
                        <option value="outlook.com">outlook.com</option>
                        <option value="yahoo.com">yahoo.com</option>
                        <option value="직접입력">직접입력</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>주소</p></h3>
                  <div className="inputRow">
                    <input value={address} className="inputdefault" type="text"
                      readOnly onClick={() => setIsViewAddress(true)} />
                    <button type="button" onClick={() => setIsViewAddress(true)}
                      style={{ marginTop:'5px', padding: '8px 16px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', cursor: 'pointer' }}>
                      주소 검색
                    </button>
                  </div>
                </div>
                {isViewAddress && (
                  <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', padding: '20px', borderRadius: 'var(--r)', width: '500px' }}>
                      <DaumPostcodeEmbed onComplete={onCompletePost} />
                      <button onClick={() => setIsViewAddress(false)} style={{ marginTop: '10px', padding: '8px 16px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', cursor: 'pointer' }}>닫기</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>현재 교회</p></h3>
                  <div className="inputRow">
                    <input value={church} className="inputdefault" type="text" placeholder="예: 사랑의교회"
                      onChange={(e) => setChurch(e.target.value)} />
                  </div>
                </div>
                <div className="inputBox">
                  <h3 className='title'><p>교단</p></h3>
                  <div className="inputRow">
                    <input value={religiousbody} className="inputdefault" type="text" placeholder="예: 예장합동"
                      onChange={(e) => setReligiousbody(e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="inputCoverTwoColumns">
                <div className="inputBox">
                  <h3 className='title'><p>직분</p></h3>
                  <div className="inputRow">
                    <input value={dutyTitle} className="inputdefault" type="text" placeholder="예: 목사, 전도사"
                      onChange={(e) => setDutyTitle(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          </section>


          {/* 학력 상세 */}
          <section>
            <h1 className='section_title'>학력</h1>
            <div className='inputCover'>
              {educationItems.map((item, index) => (
                <div key={index} style={{ borderTop: '1px solid var(--rule-2)', padding: '20px 0', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4>학력 {index + 1}</h4>
                    <button type="button" onClick={() => removeEducationItem(index)}
                      style={{ background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', padding: '5px 10px', cursor: 'pointer' }}>
                      <CiCircleMinus size={20} />
                    </button>
                  </div>
                  <div className="inputCoverTwoColumns">
                    <div className="inputBox">
                      <h3 className='title'><p>학교명</p></h3>
                      <div className="inputRow">
                        <input value={item.schoolName} className="inputdefault" type="text"
                          onChange={(e) => {
                            const newItems = [...educationItems];
                            newItems[index].schoolName = e.target.value;
                            setEducationItems(newItems);
                          }} />
                      </div>
                    </div>
                    <div className="inputBox">
                      <h3 className='title'><p>기간</p></h3>
                      <div className="inputRow" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <input 
                            value={item.startDate ? item.startDate.split('-')[0] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="년"
                            maxLength={4}
                            style={{ width: '80px' }}
                            onChange={(e) => {
                              const year = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                              const newItems = [...educationItems];
                              const month = item.startDate ? item.startDate.split('-')[1] || '' : '';
                              newItems[index].startDate = year && month ? `${year}-${month}` : year || '';
                              setEducationItems(newItems);
                            }} />
                          <input 
                            value={item.startDate ? item.startDate.split('-')[1] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="월"
                            maxLength={2}
                            style={{ width: '60px' }}
                            onChange={(e) => {
                              const month = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                              const newItems = [...educationItems];
                              const year = item.startDate ? item.startDate.split('-')[0] || '' : '';
                              newItems[index].startDate = year && month ? `${year}-${month}` : month || '';
                              setEducationItems(newItems);
                            }} />
                        </div>
                        <span style={{ fontSize: '16px' }}>~</span>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <input 
                            value={item.endDate ? item.endDate.split('-')[0] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="년"
                            maxLength={4}
                            style={{ width: '80px' }}
                            onChange={(e) => {
                              const year = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                              const newItems = [...educationItems];
                              const month = item.endDate ? item.endDate.split('-')[1] || '' : '';
                              newItems[index].endDate = year && month ? `${year}-${month}` : year || '';
                              setEducationItems(newItems);
                            }} />
                          <input 
                            value={item.endDate ? item.endDate.split('-')[1] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="월"
                            maxLength={2}
                            style={{ width: '60px' }}
                            onChange={(e) => {
                              const month = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                              const newItems = [...educationItems];
                              const year = item.endDate ? item.endDate.split('-')[0] || '' : '';
                              newItems[index].endDate = year && month ? `${year}-${month}` : month || '';
                              setEducationItems(newItems);
                            }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="inputCoverTwoColumns">
                    <div className="inputBox">
                      <h3 className='title'><p>전공학과</p></h3>
                      <div className="inputRow">
                        <input value={item.major} className="inputdefault" type="text"
                          onChange={(e) => {
                            const newItems = [...educationItems];
                            newItems[index].major = e.target.value;
                            setEducationItems(newItems);
                          }} />
                      </div>
                    </div>
                    <div className="inputBox">
                      <h3 className='title'><p>현재 상태</p></h3>
                      <div className="inputRow">
                        <div className='checkTextRow'>
                          {['졸업', '재학', '휴학', '중퇴', '수료'].map((statusItem) => (
                            <div key={statusItem} className='checkTextBox' 
                              onClick={() => {
                                const newItems = [...educationItems];
                                newItems[index].status = statusItem;
                                setEducationItems(newItems);
                              }}>
                              <input className="checkbox" type="radio" name={`education-status-${index}`}
                                value={statusItem}
                                checked={item.status === statusItem}
                                onChange={() => {
                                  const newItems = [...educationItems];
                                  newItems[index].status = statusItem;
                                  setEducationItems(newItems);
                                }} />
                              <span className="checkmark" />
                              <div className='imgtextBox'>
                                <p className={item.status === statusItem ? 'selected' : ''}>{statusItem}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addEducationItem}
                style={{ padding: '10px 20px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', cursor: 'pointer' }}>
                학력 추가
              </button>
            </div>
          </section>

          {/* 경력 상세 */}
          <section>
            <h1 className='section_title'>경력</h1>
            <div className='inputCover'>
              {careerItems.map((item, index) => (
                <div key={index} style={{ borderTop: '1px solid var(--rule-2)', padding: '20px 0', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4>경력 {index + 1}</h4>
                    <button type="button" onClick={() => removeCareerItem(index)}
                      style={{ background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', padding: '5px 10px', cursor: 'pointer' }}>
                      <CiCircleMinus size={20} />
                    </button>
                  </div>
                  <div className="inputCoverTwoColumns">
                    <div className="inputBox">
                      <h3 className='title'><p>교회명</p></h3>
                      <div className="inputRow">
                        <input value={item.companyName} className="inputdefault" type="text"
                          onChange={(e) => {
                            const newItems = [...careerItems];
                            newItems[index].companyName = e.target.value;
                            setCareerItems(newItems);
                          }} />
                      </div>
                    </div>
                    <div className="inputBox">
                      <h3 className='title'><p>기간</p></h3>
                      <div className="inputRow" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <input 
                            value={item.startDate ? item.startDate.split('-')[0] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="년"
                            maxLength={4}
                            style={{ width: '80px' }}
                            onChange={(e) => {
                              const year = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                              const newItems = [...careerItems];
                              const month = item.startDate ? item.startDate.split('-')[1] || '' : '';
                              newItems[index].startDate = year && month ? `${year}-${month}` : year || '';
                              setCareerItems(newItems);
                            }} />
                          <input 
                            value={item.startDate ? item.startDate.split('-')[1] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="월"
                            maxLength={2}
                            style={{ width: '60px' }}
                            onChange={(e) => {
                              const month = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                              const newItems = [...careerItems];
                              const year = item.startDate ? item.startDate.split('-')[0] || '' : '';
                              newItems[index].startDate = year && month ? `${year}-${month}` : month || '';
                              setCareerItems(newItems);
                            }} />
                        </div>
                        <span style={{ fontSize: '16px' }}>~</span>
                        <div style={{ width: '48%', display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <input 
                            value={item.endDate ? item.endDate.split('-')[0] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="년"
                            maxLength={4}
                            style={{ width: '80px' }}
                            onChange={(e) => {
                              const year = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
                              const newItems = [...careerItems];
                              const month = item.endDate ? item.endDate.split('-')[1] || '' : '';
                              newItems[index].endDate = year && month ? `${year}-${month}` : year || '';
                              setCareerItems(newItems);
                            }} />
                          <input 
                            value={item.endDate ? item.endDate.split('-')[1] || '' : ''} 
                            className="inputdefault" 
                            type="text"
                            placeholder="월"
                            maxLength={2}
                            style={{ width: '60px' }}
                            onChange={(e) => {
                              const month = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                              const newItems = [...careerItems];
                              const year = item.endDate ? item.endDate.split('-')[0] || '' : '';
                              newItems[index].endDate = year && month ? `${year}-${month}` : month || '';
                              setCareerItems(newItems);
                            }} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="inputCoverTwoColumns">
                    <div className="inputBox">
                      <h3 className='title'><p>구분</p></h3>
                      <div className="inputRow">
                        <div className='checkTextRow'>
                          {CAREER_EMPLOY_TYPES.map((typeItem) => {
                            const selected = splitComma(item.employType);
                            const isChecked = selected.includes(typeItem);
                            return (
                              <div key={typeItem} className='checkTextBox'
                                onClick={() => {
                                  const next = isChecked ? selected.filter((v) => v !== typeItem) : [...selected, typeItem];
                                  const newItems = [...careerItems];
                                  newItems[index] = { ...newItems[index], employType: CAREER_EMPLOY_TYPES.filter((v) => next.includes(v)).join(', ') };
                                  setCareerItems(newItems);
                                }}>
                                <input className="checkbox" type="checkbox" checked={isChecked} readOnly />
                                <span className="checkmark" />
                                <div className='imgtextBox'>
                                  <p className={isChecked ? 'selected' : ''}>{typeItem}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="inputBox">
                      <h3 className='title'><p>사역내용</p></h3>
                      <div className="inputRow">
                        <input value={item.position} className="inputdefault" type="text" placeholder="예: 유초등부사역, 찬양사역"
                          onChange={(e) => {
                            const newItems = [...careerItems];
                            newItems[index].position = e.target.value;
                            setCareerItems(newItems);
                          }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <button type="button" onClick={addCareerItem}
                style={{ padding: '10px 20px', background: 'var(--brand)', color: '#fff', border: 'none', borderRadius: 'var(--r)', cursor: 'pointer' }}>
                경력 추가
              </button>
            </div>
          </section>


          {/* 희망사항 */}
          <section>
            <h1 className='section_title'>희망사항</h1>
            <div className='inputCover'>
              <div className="inputBox">
                <h3 className='title'><p>희망 유형</p></h3>
                <div className="inputRow">
                  <div className='checkTextRow'>
                    {/* 중복 선택 — '전임, 파트' 처럼 쉼표로 저장 */}
                    {CAREER_EMPLOY_TYPES.map((typeItem) => {
                      const isChecked = splitComma(hopeInfo.hopeType).includes(typeItem);
                      return (
                        <div key={typeItem} className='checkTextBox'
                          onClick={() => setHopeInfo({
                            ...hopeInfo,
                            hopeType: CAREER_EMPLOY_TYPES.filter((v) => splitComma(toggleComma(hopeInfo.hopeType, typeItem)).includes(v)).join(', '),
                          })}>
                          <input className="checkbox" type="checkbox" checked={isChecked} readOnly />
                          <span className="checkmark" />
                          <div className='imgtextBox'>
                            <p className={isChecked ? 'selected' : ''}>{typeItem}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>희망 지역</p></h3>
                <div className="inputRow">
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 500 }}>시/도 선택</p>
                    <div className='checkInputCover Recruit'>
                      {citydata.map((city, cityIdx) => (
                        <div
                          key={`city-${cityIdx}`}
                          className={`checkInputbox ${selectedCity === city.city ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedCity(city.city);
                          }}>
                          <p>{city.city}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {selectedCity && (
                    <div style={{ marginBottom: '20px' }}>
                      <p style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 500 }}>
                        {citydata.find(c => c.city === selectedCity)?.city} 하위 지역 선택
                      </p>
                      <div className='checkInputCover Recruit'>
                        {citydata.find(c => c.city === selectedCity)?.subarea.length === 0 ? (
                          // 세종특별자치시처럼 하위지역이 없는 경우
                          <div
                            className={`checkInputbox ${hopeInfo.hopeLocation.includes(selectedCity) ? 'selected' : ''}`}
                            onClick={() => {
                              if (hopeInfo.hopeLocation.includes(selectedCity)) {
                                setHopeInfo({ ...hopeInfo, hopeLocation: hopeInfo.hopeLocation.filter(loc => loc !== selectedCity) });
                              } else {
                                setHopeInfo({ ...hopeInfo, hopeLocation: [...hopeInfo.hopeLocation, selectedCity] });
                              }
                            }}>
                            <p>{selectedCity}</p>
                          </div>
                        ) : (
                          // 맨 앞 '전체' — '서울 전체' 로 저장, 선택 시 같은 시/도의 하위 지역은 빠지고 하위 지역을 고르면 '전체'가 빠진다
                          ['전체', ...(citydata.find(c => c.city === selectedCity)?.subarea || [])].map((subarea, subIdx) => {
                            const locationValue = `${selectedCity} ${subarea}`;
                            const allValue = `${selectedCity} 전체`;
                            const isSelected = hopeInfo.hopeLocation.includes(locationValue);
                            return (
                              <div
                                key={`subarea-${subIdx}`}
                                className={`checkInputbox ${isSelected ? 'selected' : ''}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setHopeInfo({ ...hopeInfo, hopeLocation: hopeInfo.hopeLocation.filter(loc => loc !== locationValue) });
                                  } else if (subarea === '전체') {
                                    setHopeInfo({ ...hopeInfo, hopeLocation: [...hopeInfo.hopeLocation.filter(loc => !loc.startsWith(`${selectedCity} `)), allValue] });
                                  } else {
                                    setHopeInfo({ ...hopeInfo, hopeLocation: [...hopeInfo.hopeLocation.filter(loc => loc !== allValue), locationValue] });
                                  }
                                }}>
                                <p>{subarea}</p>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                  {hopeInfo.hopeLocation.length > 0 && (
                    <div style={{ marginTop: '20px' }}>
                      <b style={{ display: 'block', marginBottom: '10px', fontSize: '16px' }}>선택된 지역:</b>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {hopeInfo.hopeLocation.map((item, idx) => (
                          <span
                            key={idx}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              backgroundColor: 'var(--brand-050)',
                              border: '1px solid var(--brand-100)',
                              borderRadius: 'var(--r)',
                              fontSize: '16px'
                            }}
                          >
                            {item}
                            <button
                              onClick={() => {
                                setHopeInfo({ ...hopeInfo, hopeLocation: hopeInfo.hopeLocation.filter(loc => loc !== item) });
                              }}
                              aria-label="삭제"
                              type="button"
                              style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer',
                                color: 'var(--brand)',
                                padding: 0,
                                lineHeight: 1,
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>희망 급여</p></h3>
                <div className="inputRow" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select value={isSalaryNegotiable ? '월' : hopeInfo.hopeSalaryPaySort} className="inputdefault" style={{ width: '80px' }}
                    disabled={isSalaryNegotiable}
                    onChange={(e) => setHopeInfo({ ...hopeInfo, hopeSalaryPaySort: e.target.value })}>
                    <option value="월">월</option>
                    <option value="연">연</option>
                  </select>
                  <input value={hopeInfo.hopeSalary} className="inputdefault" type="text" style={{ width: '150px' }}
                    disabled={isSalaryNegotiable}
                    onChange={(e) => setHopeInfo({ ...hopeInfo, hopeSalary: e.target.value })} placeholder="금액 입력" />
                  <span style={{ fontSize: '16px' }}>만원</span>
                  {/* 체크 시 hopeSalaryPaySort 를 '면접 후 협의' 로 저장 (표시 화면·서버는 이 값을 그대로 사용) */}
                  <div className='checkTextRow'>
                    <div className='checkTextBox' onClick={() => setHopeInfo({
                      ...hopeInfo,
                      hopeSalaryPaySort: isSalaryNegotiable ? '월' : '면접 후 협의',
                      hopeSalary: '',
                    })}>
                      <input className="checkbox" type="checkbox" checked={isSalaryNegotiable} readOnly />
                      <span className="checkmark" />
                      <div className='imgtextBox'>
                        <p className={isSalaryNegotiable ? 'selected' : ''}>면접 후 협의</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>희망 역할</p></h3>
                <div className="inputRow">
                  <input value={hopeInfo.hopeRoles} className="inputdefault" type="text"
                    onChange={(e) => setHopeInfo({ ...hopeInfo, hopeRoles: e.target.value })} placeholder="예: 목사, 전도사" />
                  <ExampleChips examples={HOPE_ROLE_EXAMPLES} value={hopeInfo.hopeRoles}
                    onChange={(next) => setHopeInfo({ ...hopeInfo, hopeRoles: next })} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>희망 부서</p></h3>
                <div className="inputRow">
                  <input value={hopeInfo.hopeDepartment} className="inputdefault" type="text"
                    onChange={(e) => setHopeInfo({ ...hopeInfo, hopeDepartment: e.target.value })} placeholder="예: 청년부, 찬양팀" />
                  <ExampleChips examples={HOPE_DEPARTMENT_EXAMPLES} value={hopeInfo.hopeDepartment}
                    onChange={(next) => setHopeInfo({ ...hopeInfo, hopeDepartment: next })} />
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>희망 보험</p></h3>
                <div className="inputRow">
                  <input
                    value={hopeInfo.hopeInsurance}
                    className="inputdefault"
                    type="text"
                    onChange={(e) => setHopeInfo({ ...hopeInfo, hopeInsurance: e.target.value })}
                    placeholder="보험 정보 입력"
                  />
                  <div className='subTextBox'>
                    {/* '4대보험전체' 는 네 항목을 한 번에 선택/해제 */}
                    <p className={`subText ${INSURANCE_ITEMS.every((i) => splitComma(hopeInfo.hopeInsurance).includes(i)) ? 'selected' : ''}`}
                      onClick={() => {
                        const current = splitComma(hopeInfo.hopeInsurance);
                        const allSelected = INSURANCE_ITEMS.every((i) => current.includes(i));
                        const others = current.filter((i) => !INSURANCE_ITEMS.includes(i));
                        setHopeInfo({ ...hopeInfo, hopeInsurance: (allSelected ? others : [...INSURANCE_ITEMS, ...others]).join(', ') });
                      }}>
                      4대보험전체
                    </p>
                    {INSURANCE_ITEMS.map((subItem) => (
                      <p key={subItem} className={`subText ${splitComma(hopeInfo.hopeInsurance).includes(subItem) ? 'selected' : ''}`}
                        onClick={() => setHopeInfo({ ...hopeInfo, hopeInsurance: toggleComma(hopeInfo.hopeInsurance, subItem) })}>
                        {subItem}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
              <div className="inputBox">
                <h3 className='title'><p>희망 복리후생</p></h3>
                <div className="inputRow">
                  <input
                    value={hopeInfo.hopeWelfare}
                    className="inputdefault"
                    type="text"
                    onChange={(e) => setHopeInfo({ ...hopeInfo, hopeWelfare: e.target.value })}
                    placeholder="복리후생 정보 입력"
                  />
                  <div className='subTextBox'>
                    {['퇴직금', '사택제공', '공과금', '통신비', '월차제도', '유연근무제', '칼퇴권장', '건강검진', '상여금', '휴가비', '출산휴가'].map((subItem: string, subIndex: number) => {
                      const isSelected = hopeInfo.hopeWelfare.split(', ').includes(subItem);
                      return (
                        <p
                          key={subIndex}
                          className={`subText ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            const currentList = hopeInfo.hopeWelfare ? hopeInfo.hopeWelfare.split(', ') : [];
                            if (isSelected) {
                              setHopeInfo({ ...hopeInfo, hopeWelfare: currentList.filter((i: any) => i !== subItem).join(', ') });
                            } else {
                              setHopeInfo({ ...hopeInfo, hopeWelfare: [...currentList, subItem].join(', ') });
                            }
                          }}
                        >
                          {subItem}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <SelfIntroductionEdit items={selfIntroduction} onChange={setSelfIntroduction} />

          <PortfolioEdit items={portfolioItems} onChange={setPortfolioItems} userAccount={userData.userAccount} />

          {/* 공개 설정 */}
          <section>
            <h1 className='section_title'>공개 설정</h1>
            <div className='inputCover'>
              <div className="inputBox">
                <h3 className='title'><p>이력서 공개범위</p></h3>
                <div className="inputRow">
                  {VISIBILITY_OPTIONS.map(option => (
                    <div key={option.value} style={{ marginBottom: '12px' }}>
                      <div className='checkTextRow'>
                        <div className='checkTextBox' onClick={() => chooseVisibility(option.value)}>
                          <input className="checkbox" type="checkbox" checked={visibility === option.value} readOnly />
                          <span className="checkmark" />
                          <div className='imgtextBox'>
                            <p className={visibility === option.value ? 'selected' : ''}>{option.label}</p>
                          </div>
                        </div>
                      </div>
                      <p style={{ marginTop: '4px', fontSize: '15px', color: 'var(--ink-3)', lineHeight: 1.5 }}>{option.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 기타 */}
          <section>
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ marginBottom: '20px' }}>
                <span style={{ fontSize: '18px', color: 'var(--ink-3)' }}>작성자: </span>
                <span style={{ fontSize: '18px', fontWeight: 500 }}>{userData?.userNickName || userData?.userAccount || '-'}</span>
              </div>
              <div>
                <span style={{ fontSize: '18px', color: 'var(--ink-3)' }}>저장일: </span>
                <span style={{ fontSize: '18px', fontWeight: 500 }}>{saveDate || new Date().toISOString().split('T')[0]}</span>
              </div>
            </div>
          </section>
        </div>

          {/* 본문 끝 인라인 저장 버튼 — 스크롤이 끝에 도달했을 때 노출됨 (플로팅은 숨김) — 사역자 공고 등록과 동일 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '20px 0 40px',
            }}
          >
            <div
              className="submitBtn"
              onClick={handleSave}
              style={{
                padding: '14px 36px',
                minWidth: 160,
                fontSize: 16,
                fontWeight: 'bold',
                color: '#fff',
                backgroundColor: 'var(--brand)',
                textAlign: 'center',
                borderRadius: 5,
                cursor: 'pointer',
              }}
            >
              {submitLabel}
            </div>
          </div>
        </div>
      </div>

      {/* 플로팅 저장 버튼 — 우측 하단 고정, 페이지 끝에서는 숨김(본문 끝의 인라인 버튼이 그 자리를 대체) */}
      {!isAtBottom && (
        <div
          className="floating-save-btn"
          onClick={handleSave}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '14px 28px',
            minWidth: 120,
            fontSize: 16,
            fontWeight: 'bold',
            color: '#fff',
            backgroundColor: 'var(--brand)',
            textAlign: 'center',
            borderRadius: 'var(--r)',
            cursor: 'pointer',
            zIndex: 1100,
          }}
        >
          {submitLabel}
        </div>
      )}
    </div>
  );
}

